package er.prateek.crossly.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.mode_alphabets
import crossly.shared.generated.resources.mode_alphabets_sub
import crossly.shared.generated.resources.mode_brainstorm
import crossly.shared.generated.resources.mode_brainstorm_sub
import crossly.shared.generated.resources.mode_mathematics
import crossly.shared.generated.resources.mode_mathematics_sub
import crossly.shared.generated.resources.mode_riddles
import crossly.shared.generated.resources.mode_riddles_sub
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.ui.theme.game
import er.prateek.crossly.ui.theme.mixWith
import org.jetbrains.compose.resources.StringResource

/** The four ways to play as the home hub presents them. */
enum class PlayMode(val glyph: String, val title: StringResource, val subtitle: StringResource) {
    ALPHABETS("Aa", Res.string.mode_alphabets, Res.string.mode_alphabets_sub),
    MATHEMATICS("÷+", Res.string.mode_mathematics, Res.string.mode_mathematics_sub),
    RIDDLES("?", Res.string.mode_riddles, Res.string.mode_riddles_sub),
    BRAINSTORM("✦", Res.string.mode_brainstorm, Res.string.mode_brainstorm_sub),
    ;

    /** The analytics `mode` value -- the same spelling as [GameMode.analyticsName], plus `brainstorm`. */
    val analyticsName: String
        get() = when (this) {
            ALPHABETS -> GameMode.CROSSWORD.analyticsName
            MATHEMATICS -> GameMode.MATH_CROSSWORD.analyticsName
            RIDDLES -> GameMode.RIDDLES.analyticsName
            BRAINSTORM -> "brainstorm"
        }

    companion object {
        fun of(mode: GameMode): PlayMode = when (mode) {
            GameMode.CROSSWORD -> ALPHABETS
            GameMode.MATH_CROSSWORD -> MATHEMATICS
            GameMode.RIDDLES -> RIDDLES
        }
    }
}

@Composable
fun PlayMode.tint(): Color {
    val colors = MaterialTheme.game
    return when (this) {
        PlayMode.ALPHABETS -> colors.accent
        PlayMode.MATHEMATICS -> colors.gold
        PlayMode.RIDDLES -> colors.accentAlt
        PlayMode.BRAINSTORM -> colors.flame
    }
}

/**
 * A glossy, game-style emblem for a mode: a lit disc with a bevelled rim and a highlight, the
 * mode's glyph on top. Drawn rather than an asset so it themes with the palette.
 */
@Composable
fun ModeBadge(mode: PlayMode, size: Dp, modifier: Modifier = Modifier) {
    val tint = mode.tint()
    Box(modifier = modifier.size(size), contentAlignment = Alignment.Center) {
        Canvas(modifier = Modifier.size(size)) {
            val r = this.size.minDimension / 2f
            val rim = r * 0.12f
            // Drop "slab" under the disc, matching the app's extruded card/button language.
            drawCircle(color = tint.mixWith(Color.Black, 0.45f), radius = r - rim / 2f, center = Offset(center.x, center.y + rim * 0.6f))
            drawCircle(
                brush = Brush.verticalGradient(listOf(tint.mixWith(Color.White, 0.3f), tint.mixWith(Color.Black, 0.15f))),
                radius = r - rim / 2f,
            )
            drawCircle(
                color = tint.mixWith(Color.White, 0.55f),
                radius = r - rim,
                style = Stroke(width = rim * 0.5f),
            )
            drawCircle(
                color = Color.White.copy(alpha = 0.22f),
                radius = r * 0.36f,
                center = Offset(center.x - r * 0.25f, center.y - r * 0.3f),
            )
        }
        Text(
            text = mode.glyph,
            style = TextStyle(
                fontSize = (size.value * 0.36f).sp,
                fontWeight = FontWeight.Black,
                color = Color.White,
            ),
            maxLines = 1,
        )
    }
}
